/**
 * PendientesSubsanar — Lista de reservas con voucher pendiente por subsanar.
 *
 * Solo admin/superadmin ven este dashboard. Cada reserva aquí tiene operaciones
 * bloqueadas hasta que se suba un voucher válido para el pago no-efectivo que
 * registró.
 */
import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Stack, CircularProgress,
  Alert, Chip,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SubsanarVoucherModal from './SubsanarVoucherModal';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}
function getUserRole() {
  try { return (JSON.parse(localStorage.getItem('user') || '{}').role || '').toLowerCase(); }
  catch { return ''; }
}
const fmt = (n) => new Intl.NumberFormat('es-CO').format(Math.round(Number(n) || 0));
// Formatea un ISO timestamp UTC como hora Bogotá (día-mes hora:min AM/PM).
const fmtBogota = (iso) => {
  if (!iso) return '—';
  try {
    // El backend devuelve timestamps naive UTC (sin sufijo Z). Los tratamos
    // como UTC y convertimos a America/Bogota.
    const withZ = /Z|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : iso + 'Z';
    return new Date(withZ).toLocaleString('es-CO', {
      day: '2-digit', month: 'short', year: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: true,
      timeZone: 'America/Bogota',
    });
  } catch { return iso.slice(0, 16); }
};
// Formatea una fecha ISO 'YYYY-MM-DD' como texto local ('02-Ago-26').
// IMPORTANTE: no usamos `new Date(iso)` porque JS interpreta ISO date-only
// como UTC medianoche → al convertir a Bogotá (UTC-5) retrocede un día.
// Parseamos por componentes y construimos Date local.
const fmtFecha = (iso) => {
  if (!iso) return '—';
  try {
    const soloFecha = iso.slice(0, 10);       // 'YYYY-MM-DD'
    const [y, m, d] = soloFecha.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('es-CO', {
      day: '2-digit', month: 'short', year: '2-digit',
    });
  } catch { return iso.slice(0, 10); }
};

export default function PendientesSubsanar() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reservaSubsanar, setReservaSubsanar] = useState(null);
  const role = getUserRole();
  const puedeVer = ['admin', 'superadmin'].includes(role);

  const cargar = () => {
    if (!puedeVer) { setLoading(false); return; }
    setLoading(true); setError(null);
    fetch(`${API_BASE}/turismo/vouchers/pendientes-subsanar?empresa_id=1`,
      { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject('Error'))
      .then(d => setItems(d.items || []))
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false));
  };
  useEffect(cargar, [puedeVer]);

  if (!puedeVer) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Solo admin/superadmin puede ver este reporte.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <WarningAmberIcon sx={{ fontSize: 32, color: '#dc2626' }} />
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#0f172a' }}>
            Reservas con voucher pendiente por subsanar
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: '#64748b' }}>
            Registraron un pago con método no-efectivo pero no tienen voucher válido.
            Operaciones bloqueadas hasta subsanar (embarque, edición, pagos, cancelación).
          </Typography>
        </Box>
      </Stack>

      <Card sx={{ borderRadius: 3, mb: 2, bgcolor: '#fef2f2', borderLeft: '4px solid #dc2626' }}>
        <CardContent sx={{ py: 1.5 }}>
          <Typography sx={{ fontSize: 13, color: '#991b1b', fontWeight: 600 }}>
            Total pendiente de subsanar: {items.length} reserva{items.length === 1 ? '' : 's'}
          </Typography>
        </CardContent>
      </Card>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
      {error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && (
        <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: 'auto' }}>
          {/* Responsivo por breakpoint — priorizamos código+cliente+neto siempre visibles:
              xs (< 600):  Reserva · Cliente · Neto ← lo esencial (pantalla móvil)
              sm (≥ 600):  + Fecha pasadía · Estado
              md (≥ 900):  + Agencia · Creada por · PVP
              lg (≥ 1200): + Fecha creación · Saldo (todo visible) */}
          <Table size="small" sx={{ minWidth: { xs: 400, md: 900 } }}>
            <TableHead sx={{ bgcolor: '#f8fafc' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Reserva</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Cliente</TableCell>
                <TableCell sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }}>Agencia</TableCell>
                <TableCell sx={{ fontWeight: 700, display: { xs: 'none', lg: 'table-cell' } }}>
                  Creada (COL)
                </TableCell>
                <TableCell sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }}>Creada por</TableCell>
                <TableCell sx={{ fontWeight: 700, display: { xs: 'none', sm: 'table-cell' } }}>Fecha pasadía</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }}>PVP</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Neto Entretur</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, display: { xs: 'none', lg: 'table-cell' } }}>
                  Saldo
                </TableCell>
                <TableCell sx={{ fontWeight: 700, display: { xs: 'none', sm: 'table-cell' } }}>Estado</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4, color: '#64748b' }}>
                    🎉 No hay reservas pendientes por subsanar.
                  </TableCell>
                </TableRow>
              )}
              {items.map(it => (
                <TableRow key={it.id} hover
                  onClick={() => setReservaSubsanar(it.id)}
                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f0fdfa' } }}>
                  <TableCell sx={{ fontWeight: 600, color: '#0891b2', fontSize: 12.5 }}>
                    {it.codigo_reserva}
                    {/* En xs mostramos info compacta debajo (fecha + creador) porque las columnas se ocultan */}
                    <Box sx={{ display: { xs: 'block', sm: 'none' }, fontSize: 10, color: '#64748b', fontWeight: 400, mt: 0.25 }}>
                      {fmtFecha(it.fecha_salida)} · {it.creador_nombre || '—'}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontSize: 12.5 }}>{it.nombre_cliente}</TableCell>
                  <TableCell sx={{ color: '#475569', display: { xs: 'none', md: 'table-cell' }, fontSize: 12 }}>
                    {it.agencia_nombre || '—'}
                  </TableCell>
                  <TableCell sx={{ fontSize: 11.5, color: '#475569', display: { xs: 'none', lg: 'table-cell' } }}>
                    {fmtBogota(it.created_at)}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, display: { xs: 'none', md: 'table-cell' } }}>
                    {it.creador_nombre || it.creador_email || (
                      <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>(sin registro)</span>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, display: { xs: 'none', sm: 'table-cell' } }}>
                    {fmtFecha(it.fecha_salida)}
                  </TableCell>
                  <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    ${fmt(it.precio_total)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#0d9488' }}>
                    ${fmt(it.neto_entretur)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: it.saldo_pendiente > 0 ? '#d97706' : '#94a3b8', display: { xs: 'none', lg: 'table-cell' } }}>
                    ${fmt(it.saldo_pendiente)}
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Chip label={it.estado} size="small"
                      sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 600 }} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {reservaSubsanar && (
        <SubsanarVoucherModal
          reservaId={reservaSubsanar}
          onClose={() => setReservaSubsanar(null)}
          onSubsanado={() => { setReservaSubsanar(null); cargar(); }}
        />
      )}
    </Box>
  );
}
